import { fetcher } from '@common/utils/fetcher';
import { type CommonCursorResponse } from '@common/utils/fetcher';

import { type DetailResponse } from './types';
import { type User } from './UsersService';

export interface Message {
  id: number;
  match_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  read_at: string | null;
  is_read: boolean;
  sender_name: string;
}

export interface Match extends User {
  match_id: string;
  match_date: string;
  unread_count: number;
  last_message: Message | null;
  last_message_date: string | null;
  is_unread?: boolean;
  blocked_by_me: boolean;
  blocked_me: boolean;
}

export type GetMessagesParams = {
  after?: string;
  before?: string;
  limit?: number;
};

export type GetMatchesParams = {
  after?: string;
  limit?: number;
};

type GetMatchesResponse = {
  matches: Match[];
  pagination: CommonCursorResponse;
};

export type GetMessagesResponse = {
  messages: Message[];
  unreadCount: number;
  lastMessage: Message | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
};

export type PostMessageParams = {
  matchId: string;
  content: string;
};

export type PostMessageResponse = {
  message: Message;
};

export type GetMatchByUserIdParams = {
  userId: string;
};

export type GetMatchByUserIdResponse = {
  matchId: string;
};

export type PostSwipeParams = {
  target_user_id: string;
  action: 'like' | 'pass';
  isLikeBack?: boolean;
};

export type PostSwipeResponse = DetailResponse | void;

export type PutReadMessagesParams = {
  matchId: string;
};

export type PutReadMessagesResponse = null;

export type DeleteMatchByIdParams = {
  matchId: string;
};

export type DeleteMatchByIdResponse = {
  status: 'success';
  data: {
    blocked: boolean;
    matchId: string;
  };
};

export type PutBlockMatchByIdParams = {
  matchId: string;
  blocked: boolean;
};

export type PutBlockMatchByIdResponse = {
  status: 'success';
  data: {
    blocked: boolean;
    matchId: string;
  };
};

export const getMessages = ({
  matchId,
  queryParams,
}: {
  matchId: string;
  queryParams?: GetMessagesParams;
}) => {
  return fetcher<GetMessagesResponse>({
    url: `/matches/${matchId}/messages`,
    method: 'GET',
    params: queryParams,
  });
};

export const getMatches = (queryParams?: GetMatchesParams) => {
  return fetcher<GetMatchesResponse>({
    url: '/matches',
    method: 'GET',
    params: queryParams,
  });
};

export const postMessage = ({ matchId, content }: PostMessageParams) => {
  return fetcher<PostMessageResponse>({
    url: `/matches/${matchId}/messages`,
    method: 'POST',
    data: {
      matchId,
      content,
    },
  });
};

export const getMatchByUserId = ({ userId }: GetMatchByUserIdParams) => {
  return fetcher<GetMatchByUserIdResponse>({
    url: `/match/${userId}`,
    method: 'GET',
  });
};

export const postSwipe = ({ target_user_id, action }: PostSwipeParams) => {
  return fetcher<PostSwipeResponse>({
    url: '/swipe',
    method: 'POST',
    data: {
      target_user_id,
      action,
    },
  });
};

export const putReadMessages = ({ matchId }: PutReadMessagesParams) => {
  return fetcher<PutReadMessagesResponse>({
    url: `/matches/${matchId}/messages/read`,
    method: 'PUT',
  });
};

export const deleteMatchById = ({ matchId }: DeleteMatchByIdParams) => {
  return fetcher<DeleteMatchByIdResponse>({
    url: `/matches/${matchId}`,
    method: 'DELETE',
  });
};

export const putBlockMatchById = ({
  matchId,
  blocked,
}: PutBlockMatchByIdParams) => {
  return fetcher<PutBlockMatchByIdResponse>({
    url: `/matches/${matchId}/block`,
    method: 'PUT',
    data: {
      blocked,
    },
  });
};
